
import { cartGet, cartAdd, cartGetCurrent, cartUpdate, cartChange } from '@process-creative/slate-theme-tools';

import { StateDependant, bindAssignedElements, generateDependencies } from './utils';
import { Option, StandardOptionData, OptionDependencyData, DefaultStepOptionState } from './types';

export const CLASS_OPEN = 'is-bundle-builder-open';
const formatOptionPrice = (price:string) => {
  price = price.replace('$','');
  if (price.includes('%')) return `${price}`; // Keep as-is if it's a percentage
  if (parseInt(price) <= 0) return `-$${price.replace(/\D/gi, '')}`;
  else return `$${price}`;
}

/**
 * IMPORTANT
 * 240508 -  Added icon_double_width to database. Need to add additional class on option based on this. When class is applied, Make the tile double width
 */
const getOptionMarkup = (step_id, data:Option) => {
  const template = document.createElement('template');

  const iconData = Array.isArray(data.icon) ? data.icon[0] : data.icon;

  template.innerHTML = `
    <style>
      :host {
        display: block;
        background: #FCFCFB;
        border: 1px solid #FCFCFB;
        position: relative;
        padding: 1.5rem .75rem 2rem;
        border-radius: 1.5rem;
        flex: 1;
        width: 41vw;
        max-width: 10.0625rem;
        cursor: pointer;
      }

      :host(:first-child),
      :host(:not(.is-hidden):first-of-type) {
        width: calc(50vw - 1.625rem);
      }

      :host(:last-child),
      :host(:not(.is-hidden):last-of-type) {
        width: calc(50vw - 1.625rem);
      }

      :host(.is-double-width) {
        flex: 0 0 100%;
      }

      :root {

      }

      :host(.is-selected) {
        border: 1px solid #163021;
        outline: 6px solid #F5F2F0;
        outline-offset: -7px;  
      }

      :host(.is-hidden) {
        display: none;
      }

      .label {
        display: flex;
        align-items: center;
        flex-direction: column;
        background-color: #FCFCFB;
        transition: all 0.3s;
        cursor: pointer;
      }

      .icon { 
        width: 100%;
        height: 100%;
        object-fit: contain;
        position: absolute;
        top: 0;
        left: 0;
        mix-blend-mode: multiply;
      }

      .icon-wrapper {
        width: 6.5rem;
        height: 6.5rem;
        position: relative;
      }

      .title {
        color: #163021;
        font-size: .875rem;
        font-weight: 500;
        line-height: 1.25rem;
        letter-spacing: .0175rem;
        margin: 1rem 0 0;
      }

      .cost {
        margin-top: .5rem;
        color: #3f7355;
        font-size: .875rem;
        font-weight: 500;
        line-height: 1.25rem;
        letter-spacing: .0175rem;
      }

      .dependency-text {
        background-color: #c84242;
        color: #fcfcfb;
        opacity: 0;
        pointer-events: none;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 10;
        width: 70%;
        transition: opacity 0.3s;
        padding: .5rem 1rem;
        border-radius: .5rem;
        font-size: .875rem;
        font-weight: 500;
        line-height: 1.25rem;
        letter-spacing: .0175rem;
        cursor: not-allowed;
      }

      :host(.is-disabled) .label {
        opacity: 0.5;
      }
      :host(.is-disabled) .label:hover + .dependency-text {
        opacity: 1;
      }

      @media only screen and (min-width: 395px) {
        :host,
        :host(:not(.is-hidden):first-of-type),
        :host(:not(.is-hidden):last-of-type) {
          flex: 0 0 10.6875rem;
          width: 10.6875rem;
          max-width: 10.6875rem;
        }
      }

      @media only screen and (min-width: 1100px) {

        :host,
        :host(:not(.is-hidden):first-of-type),
        :host(:not(.is-hidden):last-of-type) {
          flex: 0 0 11.6875rem;
          width: 11.6875rem;
          max-width: 11.6875rem;
        }

        .icon-wrapper {
          width: 8.375rem;
          height: 8.375rem;
        }

        .title {
          color: #163021;
          font-size: 1rem;
          font-weight: 500;
          line-height: 1.5rem;
          letter-spacing: .02rem; 
          margin: 1.25rem 0 0;
          text-align: center;
        }
      }

    </style>

    <input class="input" hidden type="radio" id="${data.option_id}" name="${step_id}">
    <label class="label" for="${data.option_id}">
      <div class="icon-wrapper">${iconData ? `<img class="icon" src="${iconData.url}">` : ''}</div>
      <h5 class="title">${data.title}</h5>
      ${data.cost ? `<span class="cost">${formatOptionPrice(data.cost)}</span>` : ''}
    </label>
    ${data.dependency_text.length ? `<span class="dependency-text">${data.dependency_text}</span>` : ''}
  `;
  
  return template;
}

export class CustomiserOption extends StateDependant {

  private data:Option;
  private option_id:string;
  
  private option_title:string;
  private model_id:string;
  private icon:string;
  
  private hide_on_double_configuration:boolean;
  private step_type:string; // may not need this in future
  private measurement_option:boolean;

  public parentSection:HTMLElement|null;
  public cost:string;

  private step_id:string|null = this.getAttribute('step-id');


  private isSelected:boolean = false;
  constructor() {
    super();

    const data = this.getAttribute('data');

    if (!this.step_id) {
      console.error('Need to supply a step-id for this option!', this);
      return;
    }
    if (!data) {
      console.error('Need to supply some data for this option!', this);
      return;
    }
    
    this.data = JSON.parse(data)!;
    this.dependencies = generateDependencies(this.data);
    this.option_id = this.data.option_id;
    this.option_title = this.data.title;
    this.model_id = this.data.model_id;
    this.icon = this.data.icon?.url;
    this.default_state = this.data.default_option_state;
    this.cost = this.data.cost ? this.data.cost : "0";
    this.parentSection = this.closest('customiser-section');

    const shadowRoot = this.attachShadow({ mode: 'open' });
    const template = getOptionMarkup(this.step_id, this.data);
    shadowRoot.appendChild(template.content.cloneNode(true));

    this.optionLabel = shadowRoot.querySelector('.label');
    this.optionInput = shadowRoot.querySelector('.input');
    this.costElem = this.shadowRoot?.querySelector('.cost');

    this.addEventListener('click', () => this.handleChange());
    this.setState(this.default_state);
  }

  setSelected(val:boolean) {
    this.isSelected = val;
    this.classList.toggle('is-selected', this.isSelected);
    this.optionInput.toggleAttribute('checked', this.isSelected);
  }

  setCost(newCost:string) {
    this.cost = newCost;
    if (this.costElem) this.costElem.textContent = formatOptionPrice(`${this.cost}`);
  }

  handleChange(triggerIteration:number=0) {
    if (this.isDisabled) return;
    const evt = new CustomEvent('option_change', {
      detail: {
        option_id: this.option_id,
        step_id: this.step_id,
        triggerIteration: ++triggerIteration,
        icon: this.icon
      },
      bubbles: true,
      composed: true
    });

    this.dispatchEvent(evt);
  }

  triggerError(isError:boolean) {
    this.classList.toggle('is-error', isError);
  }

}

if(!customElements.get('customiser-option')) {
  customElements.define('customiser-option', CustomiserOption);
}